import React, { useState, useRef, useEffect } from "react";
import "../../assets/css/members.css";
import RowContainer from "../../Components/Common/RowContainer";
import Container from "../../Components/Common/Container";
import Label from "../../Components/Common/Label";
import ColumnContainer from "../../Components/Common/ColumnContainer";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import NewLessons from "../../Components/Lessons";
import CustomDropdown from "../../Components/Common/CustomDropdown";
import { getRequest, postRequest, baseURL } from "../../api/apiClient";
import formatDate from "../../Components/DateFormat";
import DeleteConfirmation from "../../Components/Common/DeleteConfirmation";
import { toast } from "react-toastify";
import VerticalSpace from "../../Components/VerticalSpace";
import "../../stylesheets/indexpage.css";
import PaginationComponent from "../../Components/Common/PaginationComponent";
import Toast from "../../Components/Common/Toast";
import { setParticles } from "../../redux/particlesReducerSlice";
import { setDropDown as setPackagesDropDown } from "../../redux/packageSlice";
import { useDispatch } from "react-redux";
import Loader from "../../utils/Loading";
import gsap from "gsap";
import "../../stylesheets/common.css";
import ShimmerEffect from "../../utils/ShimmerEffect";
import DeleteLoader from "../../utils/deleteLoader";
import StatusChangeConfirmation from "../../Components/Common/StatusUpdateConfirmation";
import { useDebounce } from "../../hooks/useDebounce";

const ReponsiveRow = styled(RowContainer)`
  @media (max-width: 1430px) {
    overflow-x: auto;
  }
`;

const InnerRowContainer = styled.div`
  display: flex;
  width: 100%;
  min-width: 1100px;
`;

const ScrollableRowContainer = styled(RowContainer)`
  @media (max-width: 1150px) {
    width: 100%;
    overflow-x: auto;
  }
`;

interface lessons {
  id: number;
  title: string;
  section: string;
  subTheme: boolean;
  subthemeCount: number;
  level: string;
  createdAt: string;
  active: boolean;
}

const Packages = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [lessons, setlessons] = useState<lessons[]>([]); // State to store user data
  const [selectedItem, setSelectedItem] = useState<string | null>(searchParams.get("filter") || null);
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedLessons, setSelectedLessons] = useState<number[]>([]);
  const [selectedLessonId, setSelectedLessonId] = useState<number | null>(null);

  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "0"));
  const [count, setCount] = useState(0);
  const [limit, setLimit] = useState(parseInt(searchParams.get("limit") || "5"));
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);

  const getFiltersFromItem = (item: string | null) => {
    switch (item) {
      case "Level": return { level: "A1,A2,B1,B2" };
      case "Latest update": return { updatedAt: -1 };
      case "Active": return { active: "true" };
      case "InActive": return { active: "false" };
      default: return {};
    }
  };

  const buildParams = (p: number, l: number, s: string, f: string | null): Record<string, string> => {
    const params: Record<string, string> = { page: p.toString(), limit: l.toString() };
    if (s) params.search = s;
    if (f && f !== "All") params.filter = f;
    return params;
  };

  const [filters, setFilters] = useState<{
    level?: string;
    updatedAt?: number;
    active?: string;
  }>(getFiltersFromItem(searchParams.get("filter")));

  const dropdownItems: string[] = [
    "All",
    "Level",
    "Active",
    "InActive",
    "Latest update",
  ];

  const debouncedSearch = useDebounce(searchQuery, 500);

  // Sync state when browser Back/Forward changes the URL
  useEffect(() => {
    const urlPage = parseInt(searchParams.get("page") || "0");
    const urlLimit = parseInt(searchParams.get("limit") || "5");
    const urlSearch = searchParams.get("search") || "";
    const urlFilter = searchParams.get("filter") || null;
    setPage(urlPage);
    setLimit(urlLimit);
    setSearchQuery(urlSearch);
    setSelectedItem(urlFilter);
    setFilters(getFiltersFromItem(urlFilter));
  }, [searchParams]);

  const handleSelect = (item: string) => {
    setSelectedItem(item);
  };

  // Fetch lessons data from API
  const fetchLessons = async (
    page: number,
    perPage: number,
    filters: {
      level?: string;
      updatedAt?: number;
      active?: string;
      search?: string;
    }
  ) => {
    if (!filters) {
      setLoading(true);
    } else {
      setFilterLoading(true);
    }
    try {

      const url = `${baseURL}/package/get-packages`;

      const body = {
        page,
        limit: perPage,
        ...filters,
      };

      const response = await postRequest(url, body);

      if (response?.data) {
        setlessons(response.data.data.packages);
        setCount(response.data.data.totalPackageCount);
        setPage(response.data.data.page - 1);
        setHasNextPage(response.data.data.hasNextPage);
        setHasPreviousPage(response.data.data.hasPreviousPage);
      }
      setLoading(false);
      setFilterLoading(false);
      const fetchPackages = await getRequest(
        `${baseURL}/notification-challenge/get-lesson-dropdown`
      );

      const fetchParticles = await getRequest(
        `${baseURL}/particle/get-dropdown`
      );
      dispatch(setPackagesDropDown(fetchPackages?.data?.data || []));
      dispatch(setParticles(fetchParticles?.data?.data || []));
    } catch (error) {
      console.error("Error fetching lessons:", error);
    }
  };

  useEffect(() => {
    const updatedFilters = {
      ...filters,
      search: debouncedSearch,
    };
    fetchLessons(page + 1, limit, updatedFilters);
  }, [debouncedSearch, page, limit, filters]);

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setPage(0);
    setSearchParams(buildParams(0, limit, query, selectedItem));
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
    setSearchParams(buildParams(newPage, limit, searchQuery, selectedItem));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setPage(0);
    setSearchParams(buildParams(0, newLimit, searchQuery, selectedItem));
  };

  const handleApplyFilters = () => {
    const newFilters = getFiltersFromItem(selectedItem);
    setFilters(newFilters);
    setPage(0);
    setSearchParams(buildParams(0, limit, searchQuery, selectedItem));
  };

  const handleEditPackages = (id: number) => {
    navigate(`/packages/edit-package/${id}`);
  };

  const handleCreatePackages = () => {
    navigate("/packages/create-package");
  };

  const handleStatusChange = async (id: number) => {
    try {
      const response = await getRequest(`${baseURL}/package/update-package-status/${id}`);
      if (response?.status === 200) {
        return true; // Success
      }
      return false; // Failed
    } catch (error) {
      console.error("Error updating status:", error);
      return false; // Failed
    }
  };

  const onCheckboxChange = (id: number) => {
    if (selectedLessons.includes(id)) {
      setSelectedLessons(
        selectedLessons.filter((selectedId) => selectedId !== id)
      );
    } else {
      setSelectedLessons([...selectedLessons, id]);
    }
  };

  const deleteSelectedLessons = () => {
    if (selectedLessons.length > 0) {
      setShowDeleteDialog(true);
    } else {
      Toast("Please select at least one lessons to delete.", "error");
    }
  };

  const deleteLessons = async (lessonIds: number | number[]) => {
    try {
      const payload = {
        lessonId: Array.isArray(lessonIds) ? lessonIds : lessonIds,
      };
      setIsLoading(true);
      const response = await postRequest(
        `${baseURL}/package/delete-packages`,
        payload
      );
      setIsLoading(false);
      if (response?.status === 200) {
        Toast("Packages deleted successfully", "success");
        setlessons((prevData) =>
          prevData.filter((lesson) =>
            Array.isArray(lessonIds)
              ? !lessonIds.includes(lesson.id)
              : lesson.id !== lessonIds
          )
        );
      } else {
        setIsLoading(false);
        toast.error("Failed to delete lesson(s)");
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error deleting lesson:", error);
      toast.error("An error occurred while deleting the lesson(s)");
    }
  };

  const handleDelete = async () => {
    if (selectedLessonId !== null) {
      await deleteLessons(selectedLessonId);
    } else {
      await deleteLessons(selectedLessons);
    }
    // await fetchLessons(page + 1, limit, filters);
    setSelectedLessons([]);
    setSelectedLessonId(null);
    setShowDeleteDialog(false);
  };

  const toggleDeleteDialog = () => {
    setShowDeleteDialog(!showDeleteDialog);
  };

  useEffect(() => {
    let timeLine = gsap.timeline();

    timeLine.from(".row-container", {
      opacity: 0,
      y: -40,
      duration: 0.3,
      ease: "power2.out",
    });

    timeLine.from(".user-data", {
      opacity: 0,
      y: 40,
      duration: 0.5,
      ease: "power2.out",
      stagger: 0.2,
    });
  }, [loading]);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="container-fluid">
            <div className="p-3">
              {/* Heading */}
              <div className="row mb-3 ">
                <div className="col">
                  <h2 style={{ fontWeight: "bold" }}>Special packages</h2>
                </div>
              </div>

              {/* Filter Label */}
              <div className="row mb-3">
                <div className="col">
                  <label htmlFor="filter" className="form-label">
                    Filter
                  </label>
                </div>
              </div>

              {/* Buttons Row */}
              <div className="row mb-3 all-button-responsive">
                <div className="col-3 search-input-div">
                  <input
                    type="search"
                    className="form-control rounded"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    aria-label="Search"
                    aria-describedby="search-addon"
                    style={{
                      height: "100%",
                      boxShadow: "none",
                    }}
                  />
                </div>
                <div className="col d-flex responsive">
                  <CustomDropdown
                    items={dropdownItems}
                    onSelect={handleSelect}
                    selectedItem={selectedItem || ""}
                    height="50px"
                  />
                  <button
                    type="button"
                    className="btn btn-primary ms-3 user"
                    style={{
                      backgroundColor: "#100F15",
                      borderRadius: "10px",
                      color: "#FFFFFF",
                      fontSize: "14px",
                      width: "auto",
                      padding: "14px 50px",
                    }}
                    onClick={handleApplyFilters}
                  >
                    Apply
                  </button>
                </div>
                <div className="col d-flex justify-content-end responsive">
                  {/* <button
              type="button"
              className="btn btn-outline-secondary me-2 text-center"
              style={{
                color: "#100F15",
                background: "none",
                border: "1px solid #100F15",
                borderRadius: "10px",
                fontSize: "14px",
                width: "auto",
                padding: "14px 40px",
              }}
              onClick={deleteSelectedLessons}
            >
              Delete all
            </button> */}
                  <button
                    type="button"
                    className="btn btn-success user"
                    style={{
                      backgroundColor: "#FF9E00",
                      borderRadius: "10px",
                      color: "#100F15",
                      fontSize: "14px",
                      width: "auto",
                      padding: "14px 40px",
                    }}
                    onClick={handleCreatePackages}
                  >
                    Add New
                  </button>
                </div>
              </div>
              <ScrollableRowContainer>
                <InnerRowContainer>
                  <Container width="100%">
                    <RowContainer
                      alignItem="center"
                      padding="8px 12px 8px 12px"
                      gap="8px"
                      className="row-container"
                    >
                      <RowContainer flex="1.4">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        ></Label>
                      </RowContainer>
                      <RowContainer flex="8.8">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Title
                        </Label>
                      </RowContainer>
                      {/* <RowContainer flex="8.8">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Label
                        </Label>
                      </RowContainer> */}
                      <RowContainer flex="7">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          {/* Section */}
                        </Label>
                      </RowContainer>
                      <RowContainer flex="6">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Sub-theme
                        </Label>
                      </RowContainer>
                      <RowContainer flex="5">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Level
                        </Label>
                      </RowContainer>
                      <RowContainer flex="6">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Latest updated
                        </Label>
                      </RowContainer>
                      <RowContainer flex="3.3">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Action
                        </Label>
                      </RowContainer>
                    </RowContainer>
                    {filterLoading ? (
                      <RowContainer
                        bgColor="#FFFFFF"
                        borderRadius="8px"
                        padding="10px 0px"
                      >
                        {Array.from({ length: 6 }, (_, index) => {
                          return (
                            <RowContainer
                              key={index}
                              width={index === 0 || index === 5 ? "10%" : "20%"}
                              display="flex"
                              justifyContent="center"
                            >
                              <ShimmerEffect />
                            </RowContainer>
                          );
                        })}
                      </RowContainer>
                    ) : (
                      <>
                        {lessons && lessons.length > 0 ? (
                          <ColumnContainer>
                            {lessons.map((lessons) => (
                              <NewLessons
                                key={lessons.id}
                                id={lessons.id}
                                title={lessons.title}
                                section={lessons.section || "N/A"}
                                subTheme={lessons.subTheme}
                                level={lessons.level}
                                latestdate={formatDate(lessons.createdAt)}
                                onEdit={handleEditPackages}
                                onDelete={() => {
                                  setSelectedLessonId(lessons.id);
                                  toggleDeleteDialog();
                                }}
                                isChecked={selectedLessons.includes(lessons.id)}
                                onCheckboxChange={onCheckboxChange}
                                status={lessons.active}
                                themeCount={lessons.subthemeCount}
                                onStatusChange={handleStatusChange}
                              />
                            ))}
                          </ColumnContainer>
                        ) : (
                          <>
                            <VerticalSpace height={30} />
                            <Label
                              alignItems="center"
                              display="flex"
                              color="rgba(164, 163, 167, 1)"
                              justifyContent="center"
                            >
                              No results found.
                            </Label>
                          </>
                        )}
                      </>
                    )}
                  </Container>
                </InnerRowContainer>
              </ScrollableRowContainer>
              <VerticalSpace height={10} />
              <RowContainer justifyContent="flex-end" className="user-data">
                <PaginationComponent
                  page={page}
                  rowsPerPage={limit}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  count={count}
                  hasNextPage={hasNextPage}
                  hasPreviousPage={hasPreviousPage}
                />
              </RowContainer>
            </div>
            {showDeleteDialog && (
              <DeleteConfirmation
                showDeletePopup={true}
                setShowDeletePopup={setShowDeleteDialog}
                message={`Are you sure you want to delete ${selectedLessons.length === 0
                  ? "this lessons?"
                  : selectedLessons.length === 1
                    ? "this lessons?"
                    : "these lessons?"
                  }`}
                deleteButtonLabel="Delete"
                onDelete={handleDelete}
              />
            )}

            {isLoading && <DeleteLoader />}
            <VerticalSpace height={100} />
          </div>
        </>
      )}
    </>
  );
};

export default Packages;
